'use client';

import { LogOut, Eye, EyeOff } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemLink,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  dropdownTriggerVariants
} from '@gettactic/components/src/@v2/components/ui/dropdown-menu';
import DesignSwitcher from '@/components/design/DesignSwitcher';
import { useIsMobile } from '@v2/hooks/use-mobile';
import { useAuthenticated } from '@gettactic/hooks';
import { memo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { canScheduleOthers, cn } from '@gettactic/helpers';
import { useImpersonateContext } from '@webV2/providers/ImpersonateProvider';
import { UserAvatar } from '@v2/components/display/UserAvatar/UserAvatar';
import { isTacticAppWebView } from '@gettactic/helpers/src/isTacticAppWebView';
import { invokeMobileAppAction } from '@gettactic/helpers/src/webview/invokeMobileAppAction';
import { AuthenticatedUser } from '@gettactic/api';

const hamburgerMenuItems = [
  {
    id: 'settings',
    title: 'settings',
    href: '/settings/profile',
    enabled: (authenticatedUser: AuthenticatedUser) => true
  },
  {
    id: 'admin',
    title: 'admin',
    href: '/admin',
    enabled: (authenticatedUser: AuthenticatedUser) =>
      canScheduleOthers(authenticatedUser)
  },
  {
    id: 'helpCenter',
    title: 'helpCenter',
    href: 'https://help.gettactic.com/hc/en-us',
    enabled: (authenticatedUser: AuthenticatedUser) => true
  }
];

const checkIfMenuItemIsEnabled = (
  item: (typeof hamburgerMenuItems)[number],
  authenticatedUser: AuthenticatedUser
) => {
  if (typeof item.enabled === 'function') {
    return item.enabled(authenticatedUser);
  }
  return !!item.enabled;
};

export function NavUser({
  user
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
}) {
  const isMobile = useIsMobile();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { t } = useTranslation('common');
  const {
    canImpersonate,
    isBeingImpersonated,
    toggleSidebarButton,
    isSidebarButtonEnabled,
    setIsWidgetVisible,
    stopImpersonating
  } = useImpersonateContext();

  const isAdmin = canScheduleOthers(authenticatedUser);

  const isMobileApp = isTacticAppWebView();
  const [isOpen, setIsOpen] = useState(false);

  // Use ref to track current state to avoid closure issues
  const buttonEnabledRef = useRef(isSidebarButtonEnabled);

  // Update ref when state changes
  useEffect(() => {
    buttonEnabledRef.current = isSidebarButtonEnabled;
  }, [isSidebarButtonEnabled]);

  const Avatar = memo(() => (
    <UserAvatar
      user={{
        name: user.name,
        profile_photo_url: user.avatar
      }}
      size="md"
    />
  ));

  if (isMobileApp) {
    return (
      <button
        className={'relative'}
        onClick={() => {
          invokeMobileAppAction('openInfo', undefined);
        }}
      >
        <Avatar />
      </button>
    );
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className={dropdownTriggerVariants.rounded}>
          <div className={'relative'}>
            <Avatar />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg p-0"
          variant="dropdown"
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuItemLink
            href="/settings/profile"
            className={navUserClassNames.MenuItem}
            onClick={() => setIsOpen(false)}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
          </DropdownMenuItemLink>
          <Separator className="shadow-sm" />
          <DropdownMenuGroup>
            {hamburgerMenuItems.map((item) => {
              if (!checkIfMenuItemIsEnabled(item, authenticatedUser)) return;
              return (
                <DropdownMenuItemLink
                  key={item.id}
                  href={item.href}
                  className={navUserClassNames.MenuItem}
                  onClick={() => setIsOpen(false)}
                >
                  {t(`navActions.${item.title}`)}
                </DropdownMenuItemLink>
              );
            })}
          </DropdownMenuGroup>

          <>
            {!isMobileApp && (
              <>
                <Separator />
                <DesignSwitcher isNewMenu={true} />
              </>
            )}
            {(canImpersonate || isBeingImpersonated) && (
              <div className="flex items-center justify-between pr-2">
                <DropdownMenuItem
                  className={cn(
                    navUserClassNames.MenuItem,
                    'flex-1',
                    isBeingImpersonated && 'text-red-500'
                  )}
                  onClick={() => {
                    toggleSidebarButton();
                    setTimeout(() => {
                      // wait a sec to make sure the button is visible
                      // we want it to position the widget correctly

                      if (buttonEnabledRef.current) {
                        setIsWidgetVisible(true);
                      }
                    }, 200);
                    setIsOpen(false);
                  }}
                >
                  {isSidebarButtonEnabled ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  {t('navActions.impersonate')}
                </DropdownMenuItem>
              </div>
            )}
            {isBeingImpersonated && (
              <DropdownMenuItem
                className={cn(
                  navUserClassNames.MenuItem,
                  'flex-1',
                  isBeingImpersonated && 'text-red-500'
                )}
                onClick={() => {
                  stopImpersonating();
                  setIsOpen(false);
                }}
              >
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 p-1">
                  <div className="flex h-2 w-2 items-center justify-center rounded-[3px] bg-white p-1" />
                </div>
                {t('navActions.stopImpersonating')}
              </DropdownMenuItem>
            )}
            <Separator />
          </>

          {isMobileApp && (
            <>
              <DropdownMenuItemLink
                onClick={() => {
                  invokeMobileAppAction('openInfo', undefined);
                  setIsOpen(false);
                }}
                className={navUserClassNames.MenuItem}
              >
                Info
              </DropdownMenuItemLink>
              <Separator />
            </>
          )}

          {isMobileApp ? (
            <DropdownMenuItemLink
              className={navUserClassNames.MenuItem}
              onClick={() => {
                invokeMobileAppAction('logout', undefined);
                setIsOpen(false);
              }}
            >
              <LogOut />
              {t('navActions.logout')}
            </DropdownMenuItemLink>
          ) : (
            <DropdownMenuItemLink
              className={navUserClassNames.MenuItem}
              href={`/auth/logout?returnUrl=${encodeURIComponent(
                `${window.location.origin}?logout=true`
              )}`}
              onClick={() => setIsOpen(false)}
            >
              <LogOut />
              {t('navActions.logout')}
            </DropdownMenuItemLink>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export const navUserClassNames = {
  MenuItem:
    'cursor-pointer hover:rounded-none active:rounded-none transition-none'
};

const Separator = ({ className }: { className?: string }) => (
  <DropdownMenuSeparator className={cn('m-0', className)} />
);
